<template>
<div :class="(config.layout === 'desktop') ? 'with-breadcrumb' : 'without-breadcrumb'">

    <page-loading v-if="!data_loaded" />

    <div v-else class="container-fluid">
    
    <div class="row physician" :class="{ 'inactive' : physician.status === 0 }">

        <div v-if="physician.status === 0" class="col-12">
            <div class="alert alert-primary" data-test="physicianInactiveAlert">
                This physician has been marked inactive. All records are retained, but will not show in search results, maps, reports, etc.
                <a href="#" class="alert-link" v-if="physician.can.update" @click="setActive()">Mark as Active</a>.
            </div>
        </div>

        <div class="col-12 col-md-7">        
            <provider-tasks                
                :tasks="physician.tasks_incomplete"
                :morphable_id="physician.id"
                morphable_type="App\Physician"
                :display_name="physician.display_name"
                test-data="physicianTasks"
            />

            <!-- Physician Details Card -->
            <div class="card" :class="{'pending' : pending}" id="profile">
                <img v-if="pending" class="center-me" src="/images/loading-color.svg">
                <div class="profile-header d-flex align-items-end rounded-top text-white text-3xl">

                    <span class="profile-name flex-grow-1 font-weight-bold mr-2 mb-2 leading-none" data-test="pageTitle">
                    	{{ physician.display_name }}
                    	<abbr title="linked to EMR">
                    		<i v-if="physician.system_links.length > 0" class="far fa-link text-lg ml-1"></i>
                    	</abbr>
                    </span>

                    <div class="drop-down drop-down-show dropleft mr-35" style="z-index:10">
                        <a href="#" role="button" id="physicianDropDown" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-test="contextMenu">
                            <i class="far fa-ellipsis-h text-xl text-white"></i>
                        </a>
                        <div class="dropdown-menu py-0" aria-labelledby="physicianDropDown">
                            <span class="dropdown-item" @click="setFollowUp()" data-test="tagPhysician">Tag for follow-up</span>
                            <template v-if="physician.can.update">
                                <span class="dropdown-item" @click="startEditing()" data-test="editPhysician">Edit Physician</span>
                                <span class="dropdown-item" @click="mergeInto()" data-toggle="modal" data-target="#mergeModal" data-test="mergePhysician">Merge Record To...</span>
                            </template>
                            <template v-if="physician.can.delete">
                                <span v-if="physician.status === 0" class="dropdown-item" @click="setActive()" data-test="markPhysicianActive">Mark as Active</span>
                                <span v-else class="dropdown-item" @click="setInactive()" data-test="markPhysicianInactive">Mark as inactive</span>
                                <span class="dropdown-item" @click="deletePhysician()" data-test="deletePhysician">Delete</span>
                            </template>
                        </div>
                    </div>

                    <div class="profile-image rounded-top"></div>
                </div>

                <div class="px-35 pt-2 pb-35">
                	<div class="mb-3" :class="($root.$mq === 'xs' || $root.$mq === 'sm') && editing ? '' : 'd-flex'">
                        
                        <template v-if="editing">
                            <avatar-field 
                                class="profile-avatar" style="position: relative;"
                                ref="image_field"
                                :default_url="physician.photo_url"
                                @avatar_changed="changeLocalAvatar($event)"
                            />
                            <div class="flex-grow-1">
                                <div class="d-flex mt-2 mb-1 align-items-center">
                                    <i class="fal fa-user-md text-gray-500 mr-2 fa-fw""></i>
                                    <input v-model="form.prefix" class="form-control mr-1 w-25" placeholder="Prefix" data-test="physicianPrefix">
                                    <input v-model="form.first_name" class="w-75 form-control" placeholder="First name" :class="{ 'is-invalid' : form.errors.has('first_name')}" required data-test="physicianFirstName">
                                    <has-error :form="form" field="first_name"></has-error>
                                </div>
                                <div class="d-flex mb-3 align-items-center">
                                    <i class="fal fa-user-md mr-2 fa-fw" style="opacity:0;"></i>
                                    <input v-model="form.last_name" class="w-75 form-control mr-1" placeholder="Last name" :class="{ 'is-invalid' : form.errors.has('last_name')}" required data-test="physicianLastName">
                                    <input v-model="form.suffix" class="w-25 form-control" placeholder="Suffix" data-test="physicianSuffix">
                                    <has-error :form="form" field="last_name"></has-error>
                                </div>

                                <div class="d-flex align-items-center mb-4 specialties">
                                    <i class="fal fa-stethoscope text-gray-500 mr-2 fa-fw width:18px" v-tooltip.bottom="'Specialties'"></i>                
                                    <v-select                                
                                        multiple
                                        label="label"                                
                                        :options="options.specialties"
                                        :reduce="label => label.id"
                                        v-model="form.specialty"
                                        data-test="physicianSpecs"
                                    />
                                </div>

                                <div class="mb-2">
                                    <div v-for="email in form.emails" class="d-flex align-items-center">
                                        <i class="fal fa-envelope text-gray-500 mr-2 fa-fw" v-tooltip.bottom="'Email'"></i>   
                                        <div class="w-100 d-flex">
                                            <input v-if="email.verified_at" v-model="email.email" type="email" class="form-control mb-2" disabled v-tooltip.bottom="'Verified Emails cannot be edited, but can be deleted'" >
                                            <input v-else v-model="email.email" type="email" class="form-control mb-2" placeholder="Email" data-test="physicianEmail" :data-lpignore="true" >
                                            
                                            <span
                                                v-if="!email.primary"
                                                @click="makeEmailPrimary(email)"
                                                v-tooltip.bottom="'Make Primary'"
                                                class="btn btn-sm text-gray-500 cursor-pointer leading-none float-right pt-2"
                                                data-test="makePrimary" >
                                                <i class="far fa-star text-base"></i>
                                            </span>
                                            <span v-else
                                                v-tooltip.bottom="'Is Primary'"
                                                class="btn btn-sm text-gray-500 leading-none float-right pt-2"
                                                style="cursor:default;"
                                                >
                                                <i class="fas fa-star text-base"></i>
                                            </span> 
                                            <span
                                                @click="deleteEmail(email)"
                                                v-tooltip.bottom="'Delete Email'"
                                                class="btn btn-sm text-gray-300 cursor-pointer leading-none float-right pt-2"
                                                data-test="deleteEmail" >
                                                <i class="fas fa-times-circle"></i>
                                            </span>
                                        </div>
                                    </div>   
                                    <div style="height: 25px;">
                                        <span
                                            @click="addEmail()"
                                            v-tooltip.bottom="'Add Email'"
                                            class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none float-right"
                                            data-test="addEmailButton" >
                                            <i class="far fa-plus-circle text-base" />
                                        </span> 
                                    </div>
                                </div>
                               

                                <div class="d-flex align-items-center mb-2">
                                    <i class="fal fa-users-medical text-gray-500 mr-2 fa-fw" v-tooltip.bottom="'Groups'"></i>
                                    <v-select                                
                                        multiple
                                        placeholder="Select group(s)"
                                        label="display_name"                                
                                        :options="allGroupIds"
                                        :reduce="display_name => display_name.morphable_id"
                                        v-model="form.groups"
                                        data-test="physicianGroups"
                                    />                                
                                </div> 

                                <div class="mb-2 d-flex align-items-center">              
                                    <i class="fal fa-id-card-alt text-gray-500 mr-2 fa-fw" v-tooltip.bottom="'NPI Number'"></i>
                                    <input v-model="form.npi" class="form-control" placeholder="NPI number" data-test="physicianNpi">
                                </div>

                                <div class="mb-0 form-group text-right">
                                    <button class="btn btn-ghost-secondary" @click="stopEditing()">Cancel</button>
                                    <button class="btn btn-primary" @click="updatePhysician()" data-test="savePhysicianButton">Save</button>
                                </div>                                                               
                            </div>
                        </template>


                        <template v-else>
                            <div class="profile-avatar" style="position: relative;">
                                <img v-if="image_updating" src="/images/loading-color.svg" class="center-me" style="margin-left: -8px; margin-top: -23px;">
                                <div class="rounded-circle circle-cropped avatar-xl mr-3 bg-white">
                                    <img class="img-fluid" :class="image_updating ? 'inactive' : ''" :src="physician.photo_url" alt="Avatar" >
                                </div>                    
                            </div>
                            <div class="flex-grow-1">
                                <span
                                    v-for="specialty in physician.specialties"
                                    class="badge text-base bg-teal-200 text-teal-700 mb-1 mr-1"
                                    v-html="specialty.label"
                                    data-test="physicianSpecsList"
                                />  

                                <div v-if="physician.emails.length" class="mb-3">
                                    <div v-for="email in physician.emails" class="">
                                        <a :href="'mailto:' + email.email">{{ email.email }}</a>
                                        <span v-if="email.primary" v-tooltip.top="'Primary Email'"><i class="fas fa-star text-base"></i></span>
                                        <span v-if="email.verified_at" v-tooltip.top="'Verified at ' + email.verified_at"><i class="fas fa-badge-check"></i></span> 
                                    </div>
                                </div> 

                                <div v-if="physician.groups.length" class="mb-1 d-flex align-items-baseline">
                                    <i class="fal fa-users-medical text-gray-500 mr-2" v-tooltip.top="'Groups'"></i>
                                    <div>                                        
                                        <div v-for="group in physician.groups">
                                            <router-link :to="{ name: 'groups.show', params:{ id: group.id } }">
                                                {{ group.name }}
                                            </router-link>
                                        </div>
                                    </div>
                                </div>                                                                                           
                            </div>
                        </template>

    	            </div>

    				<div class="d-flex border-top" style="margin:0 -1.25rem;">
    					<div class="flex-fill text-center mt-3 border-right">
    						<span class="text-gray-500 text-sm">Overall Rank</span>
    						<div class="d-flex justify-content-center align-items-baseline">
    							<div class="text-3xl mr-2">
    								{{ physician.overall_rank ? physician.overall_rank : '--' }}
    								<span class="text-base text-gray-500"> / {{ physician.client_metrics ? physician.client_metrics.count_physicians : 'na' }}</span>
    							</div>
    							<baby-progress-bar :level=rankProgressBar />
    						</div>
    					</div>
    					<div class="flex-fill text-center mt-3 border-right">
    						<span class="text-gray-500 text-sm">Check-Ins</span>
    						<div class="d-flex justify-content-center align-items-baseline">
    							<div class="text-3xl mr-2">
    								{{ physician.checkins.length }}
    							</div>
    							<baby-progress-bar :level=rankCheckins />
    						</div>
    					</div>
    					<div class="flex-fill text-center mt-3">
    						<span class="text-gray-500 text-sm">Last Check-in</span>
    						<div class="d-flex justify-content-center align-items-baseline">
    							<div class="text-3xl mr-2">
    								{{ sinceLastCheckin }}
    							</div>
    							<baby-progress-bar level=3 />
    						</div>
    					</div>
    				</div>
                </div>

                <div class="card-footer py-2 text-gray-500 d-flex align-items-center">
                    <template v-if="physician.user">
                        Belongs to <div class="rounded-circle circle-cropped avatar-xs mx-2"><img class="img-fluid" :src="physician.user.photo_url"></div>
                        {{ physician.user.display_name }}
                    </template>
                    <em v-else>Belongs to organization</em>
                </div>                
            </div>

            <locations-manager
                id="locations"
                :locationable_id="physician.id"
                locationable_type="Physician"
                :can_update_locations="physician.can.update"
                :locations="physician.locations"
                data-test="physicianLocations"
            />

            <physician-metrics
                id="metrics"
                v-if="physician.status"
                :physician="physician"
            />


            <notes-manager
                id="notes"
                :noteable_id="physician.id"
                noteable_type="Physician"
                :can_update="physician.can.update"
                :notes="physician.notes"
                data-test="physicianNotes"
            />

        </div>

        <div class="col-12 col-md-5 mb-3 pb-5 pb-md-0" id="checkins">
            <physician-checkins
                :morphable_id="physician.id"
                morphable_type="App\Physician"
                :display_name="physician.display_name"
                :photo_url="physician.photo_url"
                :checkins="physician.checkins"
                :can_create_checkin="physician.status ? perms.can_create_checkin : false"
            />
        </div>

        <!-- Merge Modal -->
        <div class="modal fade" id="mergeModal" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
          <div class="modal-dialog modal-lg" role="document">
          	<img v-if="merge_status === 'pending'" class="center-me" src="/images/loading-color.svg">

            <div class="modal-content">

              <div class="text-gray-500 font-weight-bold text-uppercase text-base bg-gray-100 px-3 py-25 rounded-top border-bottom">
                <span>Merge {{ physician.display_name }}'s Records</span>
                <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div class="modal-body py-0 px-3">
              	<div class="row" :class="{ 'pending' : merge_status === 'pending' }">
    				<div class="d-flex flex-column col-md-6 pt-3 pb-3">
    					<div v-if="merge_status === 'error'" class="alert alert-danger">
    						Uh oh! Something went wrong. Check your entry and try again.
    					</div>
                    	<div class="form-group">
                            Select the destination to records in to:
                            <v-select                                       
                                label="display_name"
                                v-model="dest_physician"
                                :options="allExCurrentPhysicianIds">
                            </v-select>
                        </div>

                        <div class="form-group">
                            <div class="section-label">Select items to merge</div>
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="record_merge.items" value="campaign_visits"> Campaigns
                            </div>
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="record_merge.items" value="checkins"> Checkins
                            </div>                        
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="record_merge.items" value="locations"> Locations
                            </div>
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="record_merge.items" value="notes"> Notes
                            </div>
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="record_merge.items" value="route_visits"> Routes (reqs checkins and locations)
                            </div>                                                                               
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="record_merge.items" value="specialties"> Specialties
                            </div>
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="record_merge.items" value="tasks"> Tasks
                            </div>
                            <div class="text-small text-gray-500">Unchecked items will be deleted.</div>
                        </div>

    					<div class="button-wrap pt-3 pr-3 pl-3 d-flex justify-content-between">
    						<button type="button" class="btn btn-ghost-secondary" data-dismiss="modal">Cancel</button>
                			<button type="button" class="btn btn-primary" @click="mergeSave()" :disabled="!dest_physician_display">Merge</button>
    					</div>	                    

                    </div>
                
                	<div class="merge-destination col-md-6 pt-3 pb-3">
                        <div v-if="dest_physician_display">
                        	<h3 class="text-gray-500 font-weight-bold text-lg mb-3" id="myModalLabel">Destination</h3>
                            <div class="mb-2">
                            	<span class="text-medium"><strong>{{ dest_physician_display.display_name }}</strong></span>
                            	<span v-for="specialty in dest_physician_display.specialties" class="badge badge-primary mr-1">{{ specialty.label }}</span>
                            </div>                            

                            <div v-if="dest_physician_display.location_primary && dest_physician_display.location_primary.formatted_address" class="media mb-2">
                                <i class="fas fa-map-marker-alt text-gray-500 mr-1 mt-1" style="width: 18px;"></i>
                                <div class="media-body">
                                	<span v-html="dest_physician_display.location_primary.formatted_address"></span>
                                </div>
                            </div>
                            <div class="media mb-2">
                                <i class="fal fa-check-circle text-gray-500 mr-1 mt-1" style="width: 18px;"></i>
                                <div class="media-body">
                                	<strong>{{ dest_physician_display.checkins_count }}</strong> checkins
                                </div>
                            </div>	
                            <div class="media mb-2">
                                <i class="fal fa-sticky-note text-gray-500 mr-1 mt-1" style="width: 18px;"></i>
                                <div class="media-body">
                                	<strong>{{ dest_physician_display.notes_count }}</strong> notes
                                </div>
                            </div>	                                                
                        </div>
                    </div>

                </div>

              </div>

            </div>
          </div>
        </div>

    </div>

    </div>

    <section id="bottom-menu" class="d-md-none navbar fixed-bottom bg-white text-center text-sm border-top pt-1 pb-0" style="z-index:1010">
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#profile'">
            <i class="text-secondary fal fa-user"></i><br>
            Profile
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#locations'">
            <i class="text-secondary fal fa-map-marker-alt"></i><br>
            Locations
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#metrics'">
            <i class="text-secondary fal fa-chart-bar"></i><br>
            Metrics
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#notes'">
            <i class="text-secondary fal fa-sticky-note"></i><br>
            Notes
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#checkins'">
            <i class="text-secondary fal fa-check-circle"></i><br>
            Checkins
        </a>
    </section>
</div>
</template>


<script>
    import { mapGetters, mapState } from 'vuex';
    import BreadcrumbsManager from '../../helpers/BreadcrumbManager';

    import ProviderTasks from '../../components/ProviderTasks';
    import PhysicianMetrics from '../../components/PhysicianMetrics';
    import PhysicianCheckins from '../../components/PhysicianCheckins';   
    import NotesManager from '../../components/NotesManager';
    import LocationsManager from '../../components/LocationsManager';

    import AvatarField from '../../components/AvatarField';
    import BabyProgressBar from '../../components/BabyProgressBar';
    import { Form, HasError, AlertError, AlertErrors, AlertSuccess } from 'vform';

    export default {        
        mixins: [BreadcrumbsManager],

        components: {
            ProviderTasks,
            PhysicianMetrics,
            NotesManager,
            PhysicianCheckins,
            LocationsManager,

            AvatarField,
            BabyProgressBar,
            HasError,
            AlertError,
            AlertErrors,
            AlertSuccess,
            Form
        },

        props: {
            id: {
                type: Number,
                required: true,
            }
        },

        data() {
            return {
                data_loaded: false,
                editing: false,
                new_avatar: false,
                image_updating: false,
                pending: false,
                physician: null,
                form: {},

                merge_status: 'none',
                dest_physician: null,
                dest_physician_display: null,
                record_merge: {
                    mergees: [],
                    dest_physician: null,
                    items: [
                        'notes',
                        'checkins',
                        'campaign_visits',
                        'route_visits',
                        'specialties',
                        'locations',
                        'tasks'
                    ],
                }                                
            }
        },

        created() {
        },

        activated() {
            this.fetchPhysician();
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Physicians', to: { name: 'physicians.list'} },
                { text: '[Physician Name]', to: { name: 'physicians.show'} },
            ]);
        },

        methods: {
            fetchPhysician() {
                axios.get('/api/physicians/retrieve/' + this.id ).then(({data}) => {
                    this.physician = data;
                    this.data_loaded = true;

                    this.replaceBreadcrumb({
                        find: '[Physician Name]',
                        replace: { text: this.physician.display_name }
                    });                    
                });
            },

            startEditing() {                      
                this.editing = true;

                this.form = new Form({
                    prefix:         this.physician.prefix,
                    first_name:     this.physician.first_name,
                    last_name:      this.physician.last_name,
                    suffix:         this.physician.suffix,
                    npi:            this.physician.npi,
                    emails:         this.physician.emails,
                    specialty:      this.physician.specialties.map(spec => spec.id),
                    status:         this.physician.status,
                    client_id:      this.physician.client_id,
                    image:          null,
                    groups:         this.physician.groups.map(group => group.id),
                });

                // add 1 email to start if it's empty
                if (this.form.emails.length == 0) {
                    this.addEmail();
                }
            },

            stopEditing() {
                this.editing = false;               
            },     

            addEmail() {
                this.form.emails.push({
                    email: '', 
                    primary: (this.form.emails.length == 0) ? 1 : 0,
                });
            },

            makeEmailPrimary(emailObject) {
                for (var i=0; i < this.form.emails.length; i++) {
                    if (this.form.emails[i].email == emailObject.email) {
                        this.form.emails[i].primary = 1;
                        this.form.emails[i].edited = 1;
                    } else if (this.form.emails[i].primary) {
                        this.form.emails[i].primary = 0;
                        this.form.emails[i].edited = 1;
                    }
                }
            },

            deleteEmail(emailObject) {
                var wasPrimary = false;
                for (var i=0; i < this.form.emails.length; i++) {
                    if (this.form.emails[i].email == emailObject.email) {
                        if (this.form.emails[i].primary) {
                            wasPrimary = true;
                        }
                        this.form.emails.splice(i, 1); // delete
                    }
                }
                // if that was the primary email, and there are more, just choose another one
                if (wasPrimary && this.form.emails.length > 0) {
                    this.form.emails[0].primary = 1;
                    this.form.emails[0].edited = 1;
                }

            },

            updatePhysician() {
                this.pending = true;
    	        //file uploads don't work super nice; send avatar afterwards
                if (this.new_avatar) {
                    this.updatePhysicianImage();
                }

                this.form.put('/api/physicians/' + this.physician.id).then(({data}) => {
                    this.editing = false;
                    this.physician = data;

                    //prevent flashing old image since new image hasn't saved yet
                    if (this.new_avatar) {
                        this.physician.photo_url = this.new_avatar;
                    }
                    this.$store.dispatch('updatePhysician', data);
                    this.pending = false;
                });
            },

            changeLocalAvatar($event) {
                this.new_avatar = $event;
                this.physician.photo_url = this.new_avatar;
            },

            updatePhysicianImage() {
                const formData = new FormData();
                formData.append('image', this.$refs.image_field.file);

                this.image_updating = true;

                axios.post('/api/physicians/' + this.physician.id + '/image', formData, {
                    headers: { 
                    	'Content-Type': 'multipart/form-data'
                    }
                })
                .then(({data}) => {
                    this.image_updating = false;
                    this.physician.photo_url = data;
                });
            },

            setActive() {
                this.pending = true;
                axios.patch('/api/physicians/' + this.physician.id, { status: 1 }).then(response => {
                    this.fetchPhysician();
                    this.$store.dispatch('addNewPhysician', this.physician);
                    this.pending = false;
                });
            },

            setInactive() {
                this.pending = true;
                axios.patch('/api/physicians/' + this.physician.id, { status: 0 }).then(response => {
                    this.physician.status = 0;
                    this.$store.dispatch('removePhysicians', [this.physician.id]);
                    this.pending = false;
                });
            },

            setFollowUp() {
                this.$root.$emit('set_follow_up');
            },

            mergeInto() {
                this.record_merge.mergees.push(this.physician.id);
            },

            mergeSave() {
                Swal.fire({
                    title: 'Are you sure?',
                    text: 'The selected items will be merged and this physician record will be deleted.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Merge physician',
                    cancelButtonText: 'Keep physician',
                    animation: false,
                }).then((result) => {
                	if (result.value) {
                    	this.merge_status = 'pending';                
                        axios.post('/api/physicians/merge', this.record_merge).then((data) => {
                            this.merge_status = 'complete';
                            Swal.fire({
                                toast: true,
                                position: 'top-end',
                                showConfirmButton: false,
                                timer: 3000,
                                icon: 'success',
                                title: 'Physician merged, redirecting...',
                            });                            
                            //need a redirect here
                            this.$router.push({ name: 'physicians.show', params: { id: this.dest_physician.id } });     
                            jQuery('.modal').modal('hide');
                            jQuery('.modal-backdrop').remove();
                            
                            this.$store.dispatch('removePhysicians', this.record_merge.mergees);
                        }).catch((error) => {
                        	this.merge_status = 'error';
                        });
                    }
                });
            },

            deletePhysician($event) {
                Swal.fire({
                    title: 'Are you sure?',
                    text: 'Deleting a physician also deletes all associated items (check-ins, locations, route visits, etc.). Marking a record inactive is typically preferred over deletion.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Delete physician',
                    cancelButtonText: 'Keep physician',
                    animation: false,
                }).then((result) => {
                    if (result.value) {
                        axios.delete('/api/physicians/' + this.physician.id).then(() => {
                            Swal.fire({
                                toast: true,
                                position: 'top-end',
                                showConfirmButton: false,
                                timer: 3000,
                                icon: 'success',
                                title: 'Physician deleted',
                            });                            
                            //need a redirect here
                            this.$router.push({ name: 'physicians.list' });
                            this.$store.dispatch('removePhysicians', [this.physician.id]);
                        });
                    }
                });
            },       
        },

        watch: {
            dest_physician() {
                this.record_merge.dest_physician = this.dest_physician.id;
                axios.get('/api/physicians/' + this.dest_physician.id).then(response => {
                    this.dest_physician_display = response.data;
                })
            },
        },
            
        computed: {
            ...mapGetters(['allGroupIds', 'allPhysicianIds']),
            ...mapState(['config', 'options', 'perms']),
            
        	rankCheckins() {
        		if (this.physician.checkins.length == 0) { return 0; }
        		if (this.physician.checkins.length < 2) { return 1; }
        		if (this.physician.checkins.length < 4) { return 2; }
        		if (this.physician.checkins.length < 7) { return 3; }
        		if (this.physician.checkins.length < 10) { return 4; }
        		return 5;
        	},

        	rankProgressBar() {
        		if (!this.physician.overall_rank) {
        			return 0;
        		} else if (!this.physician.client_metrics) {
                    return 0;
                } else {
        			const percentage = (this.physician.client_metrics.count_physicians - this.physician.overall_rank) / (this.physician.client_metrics.count_physicians -1);
        			
        			if (percentage < .25) { return 1; }
        			if (percentage < .50) { return 2; }
        			if (percentage < .70) { return 3; }
        			if (percentage < .90) { return 4; }
        			return 5;
        		}
        	},

            allExCurrentPhysicianIds() {
                return this.allPhysicianIds.filter(item => item.id !== this.physician.id);
            },
            sinceLastCheckin() {
            	if (this.physician.checkins[0]) {
            		return moment(this.physician.checkins[0].created_at).fromNow().split(' ').slice(0,-1).join(' ');
            	}
            	else {
            		return '--';
            	}
            },
        },           
    }
</script>

<style scoped>
    .with-breadcrumb {
        margin-top:70px;
    }

    .without-breadcrumb {
        margin-top:20px;
    }

    .specialties .dropdown {
    	width:100%;
    }

	.button-wrap {
		border-top:1px solid #e4e7ea;
		margin:0 -1rem; }

	.merge-destination {
		background:#e4e7ea;
		box-shadow:inset 7px 0 9px -7px rgba(0,0,0,0.4);
		padding-top:3rem;
	}

</style>